/**
 * "Protect on-air parts": the rundown editor's safety for a rundown that is
 * running in a production (the Marina review's edit lock, kept light — no
 * server-side locking). While a part is on air or next, an edit that would
 * change it structurally asks for confirmation first; the operator can also
 * unlock the protection for a while (`UNLOCK_MS`) and it re-locks itself.
 *
 * Which parts: the `current` and `next` part instances of every active
 * production running this rundown (their `partId`, `rundownId`), read from
 * the playout state the SPA already fetches (`api/onAir.ts`).
 *
 * Which edits count (`guardedParts`), per patch:
 *   - any field set on the part or on a piece / adlib under it (a rename, a
 *     duration, a piece's timing, a delete, a move away);
 *   - a row created in or moved into the part (a new piece on air);
 *   - a delete or move of a row holding the part (its segment).
 * Not counted: script and notes typing (`patch.text` only). Editing the
 * presenter's script on air is normal, and a confirmation per keystroke would
 * make the editor unusable; the on-air instance is a copy (§5.2) that D19's
 * "Update on air?" governs anyway.
 *
 * Pure: the tree, the on-air set and the patches in; the parts touched out.
 */
import type { RowPatch } from '@norna/domain/row.js';
import type { RowTree } from '@norna/domain/tree.js';

/** How long "Unlock" lifts the protection before it re-locks itself. */
export const UNLOCK_MS = 5 * 60_000;

export type PlayoutMark = 'onair' | 'next';

/** Part ids on air / next, as the productions running this rundown report them. */
export type OnAirParts = { readonly onAir: ReadonlySet<string>; readonly next: ReadonlySet<string> };

export const NO_ON_AIR: OnAirParts = { onAir: new Set(), next: new Set() };

/** The mark per part id (on air wins when a part is both, which two productions could make it). */
export function playoutMarks(parts: OnAirParts): ReadonlyMap<string, PlayoutMark> {
  const out = new Map<string, PlayoutMark>();
  for (const id of parts.onAir) out.set(id, 'onair');
  for (const id of parts.next) if (!out.has(id)) out.set(id, 'next');
  return out;
}

export type GuardedPart = { readonly partId: string; readonly mark: PlayoutMark };

const STRUCTURAL = ['deleted', 'parentId', 'ord'];

/** The row itself and every ancestor of `rowId` (cycle-safe). */
function chain(tree: RowTree, rowId: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let current: string | null = rowId;
  while (current !== null && !seen.has(current)) {
    seen.add(current);
    out.push(current);
    current = tree.rows.get(current)?.parentId ?? null;
  }
  return out;
}

/** The protected part a row belongs to: itself, or the part it hangs under. */
function owningPart(tree: RowTree, marks: ReadonlyMap<string, PlayoutMark>, rowId: string): string | null {
  return chain(tree, rowId).find((id) => marks.has(id)) ?? null;
}

/**
 * The on-air / next parts `patches` would change, in first-touched order,
 * each once. Empty: nothing to confirm.
 */
export function guardedParts(tree: RowTree, parts: OnAirParts, patches: readonly RowPatch[]): readonly GuardedPart[] {
  const marks = playoutMarks(parts);
  if (marks.size === 0) return [];
  const touched: string[] = [];
  const touch = (id: string | null): void => {
    if (id !== null && !touched.includes(id)) touched.push(id);
  };
  for (const patch of patches) {
    const keys = Object.keys(patch.set);
    if (keys.length === 0) continue;
    touch(owningPart(tree, marks, patch.rowId));
    const parentId = patch.set['parentId'];
    if (typeof parentId === 'string') touch(owningPart(tree, marks, parentId));
    if (keys.some((k) => STRUCTURAL.includes(k))) {
      // A segment (or any holder) deleted or moved takes its on-air part with it.
      for (const partId of marks.keys()) if (chain(tree, partId).includes(patch.rowId)) touch(partId);
    }
  }
  return touched.flatMap((partId) => {
    const mark = marks.get(partId);
    return mark === undefined ? [] : [{ partId, mark }];
  });
}

export type EditDecision = { readonly kind: 'apply' } | { readonly kind: 'ask'; readonly parts: readonly GuardedPart[] };

/** Whether an edit goes straight through or asks first: only a locked protection with protected parts touched asks. */
export function decideEdit(tree: RowTree, parts: OnAirParts, locked: boolean, patches: readonly RowPatch[]): EditDecision {
  const touched = locked ? guardedParts(tree, parts, patches) : [];
  return touched.length === 0 ? { kind: 'apply' } : { kind: 'ask', parts: touched };
}

/** Whether the protection is on at `nowMs`: it is, unless an unlock has not yet run out. */
export function isLocked(unlockedUntil: number | null, nowMs: number): boolean {
  return unlockedUntil === null || nowMs >= unlockedUntil;
}
